"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../config"
import { useLanguage } from "../context/LanguageContext"
import { useNotification } from "../context/NotificationContext"
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps"
import "./FarmDetails.css"

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyCT_w1O7dUdXlSvWCRn-DxU8e11Vd0t94k"

const FarmDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()

  const [farm, setFarm] = useState(null)
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchFarmData = async () => {
      try {
        setLoading(true)
        setError(null)

        const farmRes = await axios.get(`${API_URL}/api/farms/${id}`)
        setFarm(farmRes.data)

        const projectsRes = await axios.get(`${API_URL}/api/projects/farm/${id}`)
        setProjects(projectsRes.data)

        setLoading(false)
      } catch (err) {
        setError(err.response?.data?.msg || "Failed to load farm details")
        setLoading(false)
      }
    }

    fetchFarmData()
  }, [id])

  const handleDelete = async () => {
    if (!window.confirm(t("Are you sure you want to delete this farm?"))) return

    try {
      await axios.delete(`${API_URL}/api/farms/${id}`)
      showNotification(t("Farm deleted successfully"), "success")
      navigate("/dashboard")
    } catch (err) {
      showNotification(err.response?.data?.msg || "Failed to delete farm", "error")
    }
  }

  if (loading) {
    return (
      <div className="farm-details-loading">
        <div className="spinner"></div>
        <p>{t("Loading farm details...")}</p>
      </div>
    )
  }

  if (error || !farm) {
    return (
      <div className="farm-details-error">
        <i className="fas fa-exclamation-circle"></i>
        <p>{t(error || "Farm not found")}</p>
        <button onClick={() => navigate("/dashboard")} className="back-button">
          {t("Back to Dashboard")}
        </button>
      </div>
    )
  }

  return (
    <div className="farm-details-container" dir={direction}>
      <div className="farm-details-header">
        <div className="header-content">
          <h1>{farm.name}</h1>
          <p className="farm-code">
            {t("Farm Code")}: {farm.code}
          </p>
          <div className="farm-meta">
            <span className="farm-location">
              <i className="fas fa-map-marker-alt"></i> {farm.district || "Not specified"}, {farm.country || "Not specified"}
            </span>
            <span className="farm-coordinates">
              <i className="fas fa-compass"></i> {farm.location.coordinates[0].toFixed(6)},{" "}
              {farm.location.coordinates[1].toFixed(6)}
            </span>
          </div>
        </div>
        <div className="header-actions">
          <Link to={`/farms/${id}/edit`} className="edit-button">
            <i className="fas fa-edit"></i> {t("Edit")}
          </Link>
          <button onClick={handleDelete} className="delete-button">
            <i className="fas fa-trash"></i> {t("Delete")}
          </button>
        </div>
      </div>

      <div className="farm-details-content">
        <div className="details-card farm-info">
          <div className="card-header">
            <h2>{t("Farm Information")}</h2>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">{t("Total Area")}:</span>
                <span className="info-value">{farm.area} m²</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t("Available Area")}:</span>
                <span className="info-value">{farm.availableArea} m²</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t("Created")}:</span>
                <span className="info-value">{new Date(farm.createdAt).toLocaleDateString()}</span>
              </div>
            </div>

            {farm.description && (
              <div className="farm-description">
                <h3>{t("Description")}</h3>
                <p>{farm.description}</p>
              </div>
            )}
          </div>
        </div>

        <div className="details-card farm-projects">
          <div className="card-header">
            <h2>{t("Farm Field")}</h2>
            <Link to={`/add-project?farmId=${farm._id}`} className="add-project-button">
              <i className="fas fa-plus"></i> {t("New Field")}
            </Link>
          </div>
          <div className="card-body">
            {projects.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-seedling empty-icon"></i>
                <p>{t("No Field for this farm yet")}</p>
                <Link to={`/add-project?farmId=${farm._id}`} className="btn btn-primary">
                  {t("Create Your First Field")}
                </Link>
              </div>
            ) : (
              <div className="projects-list">
                {projects.map((project) => (
                  <div key={project._id} className="project-item">
                    <div className="project-name">
                      {project.name}
                      <span className="project-area">{project.allocatedArea} m²</span>
                    </div>
                    <div className="project-crop">
                      {project.cropType ? project.cropType.name : project.customCropType?.cropType}
                    </div>
                    <div className="project-actions">
                      <Link to={`/projects/${project._id}`} className="project-action-button" title={t("View Project")}>
                        <i className="fas fa-eye"></i>
                      </Link>
                      <Link
                        to={`/projects/${project._id}/kc-calculator`}
                        className="project-action-button"
                        title={t("Kc Calculator")}
                      >
                        <i className="fas fa-calculator"></i>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="details-card farm-map">
          <div className="card-header">
            <h2>{t("Farm Location")}</h2>
          </div>
          <div className="card-body">
            <div className="map-container" style={{ width: "100%", height: 400 }}>
              <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                <Map
                  center={{
                    lat: farm.location.coordinates[1],
                    lng: farm.location.coordinates[0],
                  }}
                  zoom={17}
                  mapTypeId="satellite"
                  gestureHandling="greedy"
                  disableDefaultUI={false}
                  zoomControl={true}
                  style={{ width: "100%", height: "100%" }}
                >
                  <Marker
                    position={{
                      lat: farm.location.coordinates[1],
                      lng: farm.location.coordinates[0],
                    }}
                    title={farm.name}
                    label={{
                      text: farm.name,
                      color: "white",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  />
                </Map>
              </APIProvider>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FarmDetails
